import { useEffect } from "react";
import { Gift, Heart, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

// Update these URLs once your apps are published (imata placeholders)
const APP_STORE_URL = "https://apps.apple.com/us/app/imata/id6756683731";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.peespees.giftmap";

const REFERRAL_CODE_KEY = "giftmap_referral_code";

export const MobileOnlyScreen = () => {
  // Capture referral code from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get("ref");
    if (refCode) {
      localStorage.setItem(REFERRAL_CODE_KEY, refCode.toUpperCase());
      console.log("Referral code captured:", refCode);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex flex-col">
      {/* Header */}
      <header className="p-6 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Gift className="w-10 h-10 text-primary" />
          <h1 className="text-3xl font-bold">imata</h1>
        </div>
        <p className="text-muted-foreground">Send Joy Nearby</p>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full space-y-8">
          {/* Hero Message */}
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Download the App to Get Started</h2>
            <p className="text-xl text-muted-foreground max-w-lg mx-auto">
              imata is available exclusively on mobile devices. Join thousands spreading joy in their communities.
            </p>
          </div>

          {/* App Store Badges */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="Download on the App Store"
                className="h-14"
              />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-[84px]"
              />
            </a>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-4 mt-12">
            <Card className="p-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Discover Nearby</h3>
              <p className="text-muted-foreground">Find people in your local area open to receiving gifts</p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Gift className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Send Joy</h3>
              <p className="text-muted-foreground">Brighten someone's day with a thoughtful gift and message</p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Track Impact</h3>
              <p className="text-muted-foreground">See the kindness you've spread and gifts you've received</p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Build Community</h3>
              <p className="text-muted-foreground">Be part of a movement making the world more generous</p>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center space-y-4 border-t">
        <div className="flex gap-6 justify-center text-sm">
          <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
            Terms of Service
          </Link>
          <Link to="/support" className="text-muted-foreground hover:text-foreground transition-colors">
            Support
          </Link>
          <Link to="/safety" className="text-muted-foreground hover:text-foreground transition-colors">
            Safety
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">© 2025 imata. All rights reserved.</p>
      </footer>
    </div>
  );
};
